/**
 * AVOS Platform Administration portal — the internal-staff SaaS control centre
 * served under /platform-admin/*. Clearly distinct from the customer product
 * (no Bookings/Accounts/CRM/Revenue). Talks to /api/platform-admin/* over fetch
 * (cookies included). Navigation: Dashboard · Customers · Subscriptions ·
 * Customer Users · Platform Health · Activity · Platform Admins. ALL
 * authorization is enforced server-side; this SPA only reflects it.
 *
 * Money is integer MINOR UNITS on the wire; {@link formatMoney} renders it.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from "react";
import { ArchMark } from "../console/ArchMark.js";

const NAV = [
  { key: "dashboard", label: "Dashboard" },
  { key: "customers", label: "Customers" },
  { key: "subscriptions", label: "Subscriptions" },
  { key: "customer-users", label: "Customer Users" },
  { key: "health", label: "Platform Health" },
  { key: "activity", label: "Activity" },
  { key: "admins", label: "Platform Admins" },
] as const;
type NavKey = (typeof NAV)[number]["key"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Json = any;

async function api(path: string, body?: unknown): Promise<{ status: number; json: Json }> {
  const res = await fetch(path, {
    method: "POST", headers: { "content-type": "application/json" },
    credentials: "include", body: JSON.stringify(body ?? {}),
  });
  let json: Json = null;
  try { json = await res.json(); } catch { json = {}; }
  return { status: res.status, json };
}

function errText(json: Json): string {
  return json?.error?.error?.message ?? "Something went wrong.";
}

/** Format integer minor units + ISO currency for display (no floating point). */
function formatMoney(minor: number, currency: string): string {
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  const major = Math.floor(abs / 100);
  const cents = abs % 100;
  const grouped = major.toLocaleString(currency === "INR" ? "en-IN" : "en-US");
  const sym = currency === "INR" ? "₹" : currency === "USD" ? "$" : currency === "EUR" ? "€" : `${currency} `;
  return `${sign}${sym}${grouped}.${cents.toString().padStart(2, "0")}`;
}

function fmtDate(v: string | null | undefined): string {
  return v ? new Date(v).toLocaleDateString() : "—";
}
function fmtDateTime(v: string | null | undefined): string {
  return v ? new Date(v).toLocaleString() : "—";
}

export function PlatformAdminApp({ initialPath }: { readonly initialPath: string }): ReactElement {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [admin, setAdmin] = useState<{ id: string; email: string; role: string } | null>(null);
  const [nav, setNav] = useState<NavKey>("dashboard");

  const checkSession = useCallback(async () => {
    const r = await api("/api/platform-admin/me");
    if (r.status === 200 && r.json.admin) { setAdmin(r.json.admin); setAuthed(true); }
    else setAuthed(false);
  }, []);
  useEffect(() => { void checkSession(); }, [checkSession]);

  const orgDetailMatch = /^\/platform-admin\/organisations\/([0-9a-f-]{36})/.exec(initialPath);

  if (authed === null) return <PortalFrame><p className="pa-muted">Loading…</p></PortalFrame>;
  if (!authed) return <PlatformLogin onSuccess={() => void checkSession()} />;

  return (
    <PortalFrame
      admin={admin} nav={nav} onNav={setNav}
      onLogout={async () => { await api("/api/platform-admin/logout"); window.location.href = "/platform-admin/login"; }}
    >
      {orgDetailMatch ? <CustomerDetail id={orgDetailMatch[1]!} role={admin?.role ?? ""} />
        : nav === "dashboard" ? <Dashboard onOpen={setNav} />
        : nav === "customers" ? <Customers role={admin?.role ?? ""} />
        : nav === "subscriptions" ? <Subscriptions role={admin?.role ?? ""} />
        : nav === "customer-users" ? <CustomerUsers role={admin?.role ?? ""} />
        : nav === "health" ? <PlatformHealth />
        : nav === "activity" ? <Activity />
        : <PlatformAdmins />}
    </PortalFrame>
  );
}

function PortalFrame({ children, admin, nav, onNav, onLogout }: {
  readonly children: ReactElement | ReactElement[];
  readonly admin?: { email: string; role: string } | null;
  readonly nav?: NavKey; readonly onNav?: (k: NavKey) => void; readonly onLogout?: () => void;
}): ReactElement {
  return (
    <div className="pa-shell">
      <aside className="pa-sidebar">
        <div className="pa-brand"><ArchMark size={22} baselineColor="#101828" /><span>AVOS</span></div>
        <p className="pa-brand__sub">Platform Administration</p>
        {nav && onNav && (
          <nav className="pa-nav" aria-label="Platform">
            {NAV.map((n) => (
              <button key={n.key} type="button" className={n.key === nav ? "pa-nav__link pa-nav__link--active" : "pa-nav__link"} onClick={() => onNav(n.key)}>{n.label}</button>
            ))}
          </nav>
        )}
        {admin && onLogout && (
          <div className="pa-sidebar__foot">
            <span className="pa-muted pa-small">{admin.email}</span>
            <span className="pa-small" style={{ color: "var(--color-gold)" }}>{admin.role}</span>
            <button type="button" className="pa-btn--ghost pa-small" onClick={onLogout}>Sign out</button>
          </div>
        )}
      </aside>
      <main className="pa-main" role="main">
        <header className="pa-topbar"><span className="pa-topbar__title">AVOS Platform Administration</span></header>
        <div className="pa-content">{children}</div>
      </main>
    </div>
  );
}

/** Generic async-data hook: {loading, error, data, reload}. */
function useData<T>(path: string, body?: unknown): { loading: boolean; error: string | null; data: T | null; reload: () => void } {
  const [state, setState] = useState<{ loading: boolean; error: string | null; data: T | null }>({ loading: true, error: null, data: null });
  const [tick, setTick] = useState(0);
  const bodyKey = JSON.stringify(body ?? {});
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    void api(path, JSON.parse(bodyKey)).then((r) => {
      if (!live) return;
      if (r.status === 200) setState({ loading: false, error: null, data: r.json as T });
      else setState({ loading: false, error: errText(r.json), data: null });
    }).catch(() => { if (live) setState({ loading: false, error: "Network error.", data: null }); });
    return () => { live = false; };
  }, [path, bodyKey, tick]);
  return { ...state, reload: () => setTick((t) => t + 1) };
}

/** Loading / error / empty wrapper. */
function Async<T>({ q, empty, children }: { readonly q: ReturnType<typeof useData<T>>; readonly empty?: ReactElement; readonly children: (d: T) => ReactElement }): ReactElement {
  if (q.loading) return <p className="pa-muted">Loading…</p>;
  if (q.error) return <div className="pa-error" role="alert">{q.error} <button type="button" className="pa-link" onClick={q.reload}>Retry</button></div>;
  if (q.data === null) return empty ?? <p className="pa-muted">No data.</p>;
  return children(q.data);
}

function PlatformLogin({ onSuccess }: { readonly onSuccess: () => void }): ReactElement {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); if (busy) return; setBusy(true); setErr(null);
    const r = await api("/api/platform-admin/login", { email, password });
    setBusy(false);
    if (r.status === 200 && r.json.ok) onSuccess(); else setErr(errText(r.json));
  }
  return (
    <div className="pa-authwrap">
      <form className="pa-authcard" onSubmit={submit} noValidate>
        <div className="pa-brand pa-brand--center"><ArchMark size={26} baselineColor="#101828" /><span>AVOS</span></div>
        <p className="pa-brand__sub pa-brand__sub--center">Platform Administration</p>
        {err && <p className="pa-error" role="alert">{err}</p>}
        <label className="pa-field"><span>Email</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" /></label>
        <label className="pa-field"><span>Password</span><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></label>
        <button type="submit" className="pa-btn" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </div>
  );
}

function Dashboard({ onOpen }: { readonly onOpen: (k: NavKey) => void }): ReactElement {
  const q = useData<Json>("/api/platform-admin/stats");
  return (
    <div>
      <h1 className="pa-h1">Platform overview</h1>
      <Async q={q}>{(d) => {
        const s = d.stats ?? {}; const subs = d.subscriptions ?? {}; const sec = d.secondary ?? {};
        return (
          <>
            <div className="pa-kpis">
              <Kpi label="Active customers" value={s.activeOrganisations ?? 0} />
              <Kpi label="Suspended" value={s.suspendedOrganisations ?? 0} />
              <Kpi label="Customer users" value={s.totalUsers ?? 0} />
              <Kpi label="Properties" value={s.totalProperties ?? 0} />
              <Kpi label="Active subscriptions" value={subs.active ?? 0} />
              <Kpi label="MRR" value={formatMoney(subs.mrrMinor ?? 0, subs.currency ?? "INR")} />
            </div>
            <div className="pa-kpis">
              <Kpi label="New customers (month)" value={sec.newThisMonth ?? 0} />
              <Kpi label="Active users (30d)" value={sec.activeUsers30d ?? 0} />
              <Kpi label="Total bookings" value={sec.bookings ?? 0} />
              <Kpi label="Channex-connected" value={sec.connectedProps ?? 0} />
            </div>
            <div className="pa-panel">
              <div className="pa-panel__head"><h2 className="pa-h2">Recent customers</h2><button type="button" className="pa-btn--ghost" onClick={() => onOpen("customers")}>View all</button></div>
              {(d.recentOrganisations ?? []).length === 0 ? <p className="pa-muted">No customers yet.</p> : (
                <table className="pa-table"><thead><tr><th>Organisation</th><th>Status</th><th>Users</th><th>Properties</th><th>Created</th></tr></thead>
                  <tbody>{d.recentOrganisations.map((o: Json) => (
                    <tr key={o.id}><td><a href={`/platform-admin/organisations/${o.id}`}>{o.name}</a></td><td><StatusBadge status={o.status} /></td><td>{o.userCount}</td><td>{o.propertyCount}</td><td>{fmtDate(o.createdAt)}</td></tr>
                  ))}</tbody></table>
              )}
            </div>
            <div className="pa-panel">
              <div className="pa-panel__head"><h2 className="pa-h2">Recent activity</h2><button type="button" className="pa-btn--ghost" onClick={() => onOpen("activity")}>View all</button></div>
              <ActivityList rows={d.activity ?? []} />
            </div>
          </>
        );
      }}</Async>
    </div>
  );
}

function Kpi({ label, value }: { readonly label: string; readonly value: string | number }): ReactElement {
  return <div className="pa-card"><span className="pa-card__label">{label}</span><span className="pa-card__value">{value}</span></div>;
}

function Customers({ role }: { readonly role: string }): ReactElement {
  const q = useData<Json>("/api/platform-admin/organisations/list");
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState(""); const [statusFilter, setStatusFilter] = useState("all");
  async function setStatus(id: string, status: string): Promise<void> {
    const verb = status === "suspended" ? "suspend" : status === "inactive" ? "deactivate" : "reactivate";
    if (!confirm(`Really ${verb} this customer? Their users will immediately lose access. No data is deleted.`)) return;
    const r = await api("/api/platform-admin/organisations/set-status", { id, status });
    if (r.status !== 200) alert(errText(r.json)); else q.reload();
  }
  return (
    <div>
      <div className="pa-panel__head"><h1 className="pa-h1">Customers</h1>
        {role === "platform_admin" && <button type="button" className="pa-btn" onClick={() => setCreating(true)}>+ Add customer</button>}</div>
      <div className="pa-toolbar">
        <input className="pa-search" placeholder="Search organisation or owner…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option><option value="active">Active</option><option value="suspended">Suspended</option><option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="pa-panel">
        <Async q={q} empty={<p className="pa-muted">No customers yet.</p>}>{(d) => {
          const term = search.trim().toLowerCase();
          const rows = (d.organisations ?? []).filter((o: Json) =>
            (statusFilter === "all" || o.status === statusFilter) &&
            (term === "" || o.name.toLowerCase().includes(term) || (o.primaryOwnerEmail ?? "").toLowerCase().includes(term) || (o.primaryOwnerName ?? "").toLowerCase().includes(term)));
          if (rows.length === 0) return <p className="pa-muted">No customers match your filters.</p>;
          return (
            <div className="pa-tablewrap"><table className="pa-table"><thead><tr>
              <th>Organisation</th><th>Primary owner</th><th>Plan</th><th>Subscription</th><th>Users</th><th>Properties</th><th>Status</th><th>Created</th><th></th>
            </tr></thead><tbody>
              {rows.map((o: Json) => (
                <tr key={o.id}>
                  <td><a href={`/platform-admin/organisations/${o.id}`}>{o.name}</a></td>
                  <td>{o.primaryOwnerEmail ?? "—"}</td>
                  <td>{o.subscription?.planName ?? "—"}</td>
                  <td>{o.subscription ? <StatusBadge status={o.subscription.status} /> : <span className="pa-muted">—</span>}</td>
                  <td>{o.userCount}</td><td>{o.propertyCount}</td>
                  <td><StatusBadge status={o.status} /></td><td>{fmtDate(o.createdAt)}</td>
                  <td className="pa-actions">{role === "platform_admin" && (o.status === "active"
                    ? <button type="button" className="pa-link-danger" onClick={() => setStatus(o.id, "suspended")}>Suspend</button>
                    : <button type="button" className="pa-link" onClick={() => setStatus(o.id, "active")}>Reactivate</button>)}</td>
                </tr>
              ))}
            </tbody></table></div>
          );
        }}</Async>
      </div>
      {creating && <AddCustomerModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); q.reload(); }} />}
    </div>
  );
}

function AddCustomerModal({ onClose, onCreated }: { readonly onClose: () => void; readonly onCreated: () => void }): ReactElement {
  const plansQ = useData<Json>("/api/platform-admin/subscriptions/list");
  const [f, setF] = useState({ name: "", ownerFirstName: "", ownerLastName: "", ownerEmail: "", phone: "", region: "ap-south-1",
    discountType: "none", discountValue: "0", subscriptionStatus: "active", startsOn: new Date().toISOString().slice(0, 10),
    tempPassword: "", tempPasswordConfirm: "", notes: "" });
  const [err, setErr] = useState<string | null>(null); const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Json | null>(null);
  const set = (k: string) => (e: Json) => setF((p) => ({ ...p, [k]: e.target.value }));

  const plan = (plansQ.data?.plans ?? [])[0] ?? null;
  const base = plan?.baseAmountMinor ?? 0; const currency = plan?.currency ?? "INR";
  const dVal = Math.max(0, Math.trunc(Number(f.discountValue) || 0));
  const effective = f.discountType === "percentage" ? Math.max(0, base - Math.round((base * Math.min(100, dVal)) / 100))
    : f.discountType === "fixed" ? Math.max(0, base - dVal) : base;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault(); if (busy) return;
    setErr(null);
    if (f.tempPassword !== "" && f.tempPassword !== f.tempPasswordConfirm) { setErr("Temporary passwords do not match."); return; }
    setBusy(true);
    const r = await api("/api/platform-admin/organisations/create", {
      name: f.name, ownerFirstName: f.ownerFirstName, ownerLastName: f.ownerLastName, ownerEmail: f.ownerEmail,
      phone: f.phone, region: f.region, planCode: plan?.code ?? "avos_launch",
      discountType: f.discountType, discountValue: dVal, subscriptionStatus: f.subscriptionStatus,
      startsOn: f.startsOn, notes: f.notes,
      ...(f.tempPassword !== "" ? { tempPassword: f.tempPassword } : {}),
    });
    setBusy(false);
    if (r.status === 200 && r.json.ok) setDone(r.json); else setErr(errText(r.json));
  }

  return (
    <div className="pa-modal" role="dialog" aria-modal="true">
      <div className="pa-modal__backdrop" onClick={onClose} />
      <div className="pa-modal__panel">
        {done ? (
          <div>
            <h2 className="pa-h2">Customer created</h2>
            {done.credentialMode === "temp_password"
              ? <p className="pa-muted">The Owner <strong>{done.ownerEmail}</strong> was created with a temporary password. Share it securely out-of-band; ask them to change it after first sign-in.</p>
              : <><p className="pa-muted">An Owner invitation was created for <strong>{done.ownerEmail}</strong>. In development use this link:</p><code className="pa-code">{`/invite/accept?token=${done.inviteToken}`}</code></>}
            <div className="pa-modal__actions"><button type="button" className="pa-btn" onClick={onCreated}>Done</button></div>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <h2 className="pa-h2">Add customer</h2>
            {err && <p className="pa-error" role="alert">{err}</p>}
            <label className="pa-field"><span>Organisation name *</span><input value={f.name} onChange={set("name")} /></label>
            <div className="pa-row">
              <label className="pa-field"><span>Owner first name *</span><input value={f.ownerFirstName} onChange={set("ownerFirstName")} /></label>
              <label className="pa-field"><span>Owner last name</span><input value={f.ownerLastName} onChange={set("ownerLastName")} /></label>
            </div>
            <label className="pa-field"><span>Owner email *</span><input type="email" value={f.ownerEmail} onChange={set("ownerEmail")} /></label>
            <div className="pa-row">
              <label className="pa-field"><span>Owner phone</span><input value={f.phone} onChange={set("phone")} /></label>
              <label className="pa-field"><span>Region</span><select value={f.region} onChange={set("region")}><option value="ap-south-1">ap-south-1 (India)</option><option value="eu-central-1">eu-central-1 (EU)</option></select></label>
            </div>
            <div className="pa-divider" />
            <div className="pa-row">
              <label className="pa-field"><span>Plan</span><input value={plan ? `${plan.name} · ${formatMoney(base, currency)}/mo` : "Loading…"} readOnly /></label>
              <label className="pa-field"><span>Billing cycle</span><input value="Monthly" readOnly /></label>
            </div>
            <div className="pa-row">
              <label className="pa-field"><span>Discount type</span><select value={f.discountType} onChange={set("discountType")}><option value="none">None</option><option value="percentage">Percentage</option><option value="fixed">Fixed amount</option></select></label>
              <label className="pa-field"><span>{f.discountType === "percentage" ? "Discount %" : f.discountType === "fixed" ? "Discount (minor units)" : "Discount value"}</span><input value={f.discountValue} onChange={set("discountValue")} disabled={f.discountType === "none"} /></label>
            </div>
            <p className="pa-muted pa-small">Effective monthly price: <strong>{formatMoney(effective, currency)}</strong></p>
            <div className="pa-row">
              <label className="pa-field"><span>Subscription start</span><input type="date" value={f.startsOn} onChange={set("startsOn")} /></label>
              <label className="pa-field"><span>Subscription status</span><select value={f.subscriptionStatus} onChange={set("subscriptionStatus")}><option value="active">Active</option><option value="trial">Trial</option></select></label>
            </div>
            <div className="pa-divider" />
            <p className="pa-muted pa-small">Owner credential: leave the temporary password blank to create an invitation link instead. If set, it is hashed (Argon2id) and never stored or logged in plaintext.</p>
            <div className="pa-row">
              <label className="pa-field"><span>Temp password (optional)</span><input type="password" value={f.tempPassword} onChange={set("tempPassword")} autoComplete="new-password" /></label>
              <label className="pa-field"><span>Confirm</span><input type="password" value={f.tempPasswordConfirm} onChange={set("tempPasswordConfirm")} autoComplete="new-password" /></label>
            </div>
            <label className="pa-field"><span>Notes (optional)</span><input value={f.notes} onChange={set("notes")} /></label>
            <div className="pa-modal__actions">
              <button type="button" className="pa-btn--ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="pa-btn" disabled={busy}>{busy ? "Creating…" : "Create customer"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Subscriptions({ role }: { readonly role: string }): ReactElement {
  const q = useData<Json>("/api/platform-admin/subscriptions/list");
  async function setStatus(tenantId: string, status: string): Promise<void> {
    if ((status === "cancelled" || status === "suspended") && !confirm(`Really ${status === "cancelled" ? "cancel" : "suspend"} this subscription?`)) return;
    const r = await api("/api/platform-admin/subscriptions/set-status", { tenantId, status });
    if (r.status !== 200) alert(errText(r.json)); else q.reload();
  }
  return (
    <div>
      <h1 className="pa-h1">Subscriptions</h1>
      <div className="pa-panel">
        <Async q={q} empty={<p className="pa-muted">No subscriptions yet.</p>}>{(d) => {
          const subs = d.subscriptions ?? [];
          if (subs.length === 0) return <p className="pa-muted">No subscriptions yet.</p>;
          return (
            <div className="pa-tablewrap"><table className="pa-table"><thead><tr>
              <th>Organisation</th><th>Plan</th><th>Base</th><th>Discount</th><th>Effective</th><th>Cycle</th><th>Status</th><th>Start</th><th></th>
            </tr></thead><tbody>
              {subs.map((s: Json) => (
                <tr key={s.id}>
                  <td>{s.organisationName}</td><td>{s.planName}</td>
                  <td>{formatMoney(s.baseAmountMinor, s.currency)}</td>
                  <td>{s.discountType === "none" ? "—" : s.discountType === "percentage" ? `${s.discountValue}%` : formatMoney(s.discountValue, s.currency)}</td>
                  <td><strong>{formatMoney(s.effectiveAmountMinor, s.currency)}</strong></td>
                  <td>{s.billingCycle}</td><td><StatusBadge status={s.status} /></td><td>{fmtDate(s.startsOn)}</td>
                  <td className="pa-actions">{role === "platform_admin" && (
                    s.status === "active"
                      ? <><button type="button" className="pa-link-danger" onClick={() => setStatus(s.tenantId, "suspended")}>Suspend</button> <button type="button" className="pa-link-danger" onClick={() => setStatus(s.tenantId, "cancelled")}>Cancel</button></>
                      : <button type="button" className="pa-link" onClick={() => setStatus(s.tenantId, "active")}>Activate</button>
                  )}</td>
                </tr>
              ))}
            </tbody></table></div>
          );
        }}</Async>
      </div>
    </div>
  );
}

function CustomerUsers({ role }: { readonly role: string }): ReactElement {
  const q = useData<Json>("/api/platform-admin/customer-users/list");
  const [search, setSearch] = useState(""); const [statusFilter, setStatusFilter] = useState("all");
  async function setStatus(u: Json, status: string): Promise<void> {
    if (status === "suspended" && !confirm(`Suspend ${u.email}? They will immediately lose access.`)) return;
    const r = await api("/api/platform-admin/customer-users/set-status", { tenantId: u.organisationId, userId: u.id, status });
    if (r.status !== 200) alert(errText(r.json)); else q.reload();
  }
  return (
    <div>
      <h1 className="pa-h1">Customer users</h1>
      <div className="pa-toolbar">
        <input className="pa-search" placeholder="Search name, email or organisation…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}><option value="all">All statuses</option><option value="active">Active</option><option value="suspended">Suspended</option></select>
      </div>
      <div className="pa-panel">
        <Async q={q} empty={<p className="pa-muted">No customer users yet.</p>}>{(d) => {
          const term = search.trim().toLowerCase();
          const rows = (d.users ?? []).filter((u: Json) =>
            (statusFilter === "all" || u.status === statusFilter) &&
            (term === "" || (u.email ?? "").toLowerCase().includes(term) || (u.organisationName ?? "").toLowerCase().includes(term) ||
             `${u.firstName ?? ""} ${u.lastName ?? ""}`.toLowerCase().includes(term)));
          if (rows.length === 0) return <p className="pa-muted">No users match your filters.</p>;
          return (
            <div className="pa-tablewrap"><table className="pa-table"><thead><tr>
              <th>Name</th><th>Email</th><th>Organisation</th><th>Role</th><th>Status</th><th>Last login</th><th>Created</th><th></th>
            </tr></thead><tbody>
              {rows.map((u: Json) => (
                <tr key={`${u.organisationId}-${u.id}`}>
                  <td>{`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || "—"}</td>
                  <td>{u.email}</td><td>{u.organisationName}</td><td>{u.role ?? "—"}</td>
                  <td><StatusBadge status={u.status} /></td><td>{fmtDateTime(u.lastLoginAt)}</td><td>{fmtDate(u.createdAt)}</td>
                  <td className="pa-actions">{(role === "platform_admin" || role === "platform_support") && (
                    u.status === "active"
                      ? <button type="button" className="pa-link-danger" onClick={() => setStatus(u, "suspended")}>Suspend</button>
                      : <button type="button" className="pa-link" onClick={() => setStatus(u, "active")}>Reactivate</button>
                  )}</td>
                </tr>
              ))}
            </tbody></table></div>
          );
        }}</Async>
      </div>
    </div>
  );
}

function CustomerDetail({ id, role }: { readonly id: string; readonly role: string }): ReactElement {
  const q = useData<Json>("/api/platform-admin/organisations/get", { id });
  const [tab, setTab] = useState<"overview" | "users" | "properties" | "subscription" | "activity">("overview");
  return (
    <div>
      <p><a href="/platform-admin/organisations" className="pa-link">← Customers</a></p>
      <Async q={q} empty={<p className="pa-muted">Customer not found.</p>}>{(d) => {
        const o = d.organisation; const sub = d.subscription;
        return (
          <>
            <h1 className="pa-h1">{o.name}</h1>
            <div className="pa-tabs">
              {(["overview", "users", "properties", "subscription", "activity"] as const).map((t) => (
                <button key={t} type="button" className={t === tab ? "pa-tab pa-tab--active" : "pa-tab"} onClick={() => setTab(t)}>{t[0]!.toUpperCase() + t.slice(1)}</button>
              ))}
            </div>
            {tab === "overview" && (
              <div className="pa-panel"><dl className="pa-dl">
                <div><dt>Status</dt><dd><StatusBadge status={o.status} /></dd></div>
                <div><dt>Primary owner</dt><dd>{o.primaryOwnerEmail ?? "—"}</dd></div>
                <div><dt>Region</dt><dd>{o.region}</dd></div>
                <div><dt>Users</dt><dd>{o.userCount}</dd></div>
                <div><dt>Properties</dt><dd>{o.propertyCount}</dd></div>
                <div><dt>Plan</dt><dd>{sub?.planName ?? "—"}</dd></div>
                <div><dt>Effective monthly</dt><dd>{sub ? formatMoney(sub.effectiveAmountMinor, sub.currency) : "—"}</dd></div>
                <div><dt>Created</dt><dd>{fmtDateTime(o.createdAt)}</dd></div>
                <div><dt>Organisation ID</dt><dd><code className="pa-code">{o.id}</code></dd></div>
              </dl></div>
            )}
            {tab === "users" && (
              <div className="pa-panel">{(d.users ?? []).length === 0 ? <p className="pa-muted">No users yet.</p> : (
                <table className="pa-table"><thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Property access</th><th>Last login</th><th>Created</th></tr></thead>
                  <tbody>{d.users.map((u: Json) => (<tr key={u.id}><td>{`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || "—"}</td><td>{u.email}</td><td>{u.role ?? "—"}</td><td><StatusBadge status={u.status} /></td><td>{u.propertyAccess}</td><td>{fmtDateTime(u.lastLoginAt)}</td><td>{fmtDate(u.createdAt)}</td></tr>))}</tbody></table>
              )}</div>
            )}
            {tab === "properties" && (
              <div className="pa-panel">{(d.properties ?? []).length === 0 ? <p className="pa-muted">No properties yet.</p> : (
                <table className="pa-table"><thead><tr><th>Property</th><th>Status</th><th>Units</th><th>Bookings</th><th>Channel</th><th>Created</th></tr></thead>
                  <tbody>{d.properties.map((p: Json) => (<tr key={p.id}><td>{p.name}</td><td><StatusBadge status={p.status} /></td><td>{p.units}</td><td>{p.bookings}</td><td>{p.channelStatus === "connected" ? <span className="pa-badge pa-badge--ok">connected</span> : <span className="pa-badge">not connected</span>}</td><td>{fmtDate(p.createdAt)}</td></tr>))}</tbody></table>
              )}</div>
            )}
            {tab === "subscription" && (
              <div className="pa-panel">{!sub ? <p className="pa-muted">No subscription.</p> : (
                <dl className="pa-dl">
                  <div><dt>Plan</dt><dd>{sub.planName}</dd></div>
                  <div><dt>Status</dt><dd><StatusBadge status={sub.status} /></dd></div>
                  <div><dt>Base price</dt><dd>{formatMoney(sub.baseAmountMinor, sub.currency)}</dd></div>
                  <div><dt>Discount</dt><dd>{sub.discountType === "none" ? "—" : sub.discountType === "percentage" ? `${sub.discountValue}%` : formatMoney(sub.discountValue, sub.currency)}</dd></div>
                  <div><dt>Effective price</dt><dd><strong>{formatMoney(sub.effectiveAmountMinor, sub.currency)}</strong></dd></div>
                  <div><dt>Billing cycle</dt><dd>{sub.billingCycle}</dd></div>
                  <div><dt>Start</dt><dd>{fmtDate(sub.startsOn)}</dd></div>
                </dl>
              )}</div>
            )}
            {tab === "activity" && (<div className="pa-panel"><ActivityList rows={d.activity ?? []} /></div>)}
            {role === "platform_admin" && (
              <div className="pa-panel"><div className="pa-panel__head"><h2 className="pa-h2">Lifecycle</h2></div>
                {o.status === "active"
                  ? <button type="button" className="pa-link-danger" onClick={async () => { if (confirm("Suspend this customer? Users lose access immediately; no data is deleted.")) { await api("/api/platform-admin/organisations/set-status", { id, status: "suspended" }); q.reload(); } }}>Suspend customer</button>
                  : <button type="button" className="pa-link" onClick={async () => { await api("/api/platform-admin/organisations/set-status", { id, status: "active" }); q.reload(); }}>Reactivate customer</button>}
              </div>
            )}
          </>
        );
      }}</Async>
    </div>
  );
}

function PlatformHealth(): ReactElement {
  const q = useData<Json>("/api/platform-admin/health");
  return (
    <div>
      <div className="pa-panel__head"><h1 className="pa-h1">Platform health</h1><button type="button" className="pa-btn--ghost" onClick={q.reload}>Refresh</button></div>
      <Async q={q}>{(h) => (
        <>
          <div className="pa-health-overall"><HealthDot status={h.overall} /><span>Overall: <strong>{h.overall}</strong></span></div>
          <div className="pa-kpis">
            <HealthCard label="Application" c={h.components.application} />
            <HealthCard label="Database" c={h.components.database} />
            <HealthCard label="Channex" c={h.components.channex} note={h.components.channex.configured ? "configured" : "not configured"} />
            <HealthCard label="Booking ingestion" c={h.components.bookingIngestion} />
            <HealthCard label="Availability sync" c={h.components.availabilitySync} />
            <HealthCard label="ARI sync" c={h.components.ariSync} />
          </div>
          <div className="pa-panel"><h2 className="pa-h2">Operational detail</h2>
            <dl className="pa-dl">
              <div><dt>Last booking poll</dt><dd>{fmtDateTime(h.components.bookingIngestion.lastPoll)}</dd></div>
              <div><dt>Last successful poll</dt><dd>{fmtDateTime(h.components.bookingIngestion.lastSuccessfulPoll)}</dd></div>
              <div><dt>Last revision received</dt><dd>{fmtDateTime(h.components.bookingIngestion.lastRevision)}</dd></div>
              <div><dt>Last revision processed</dt><dd>{fmtDateTime(h.components.bookingIngestion.lastProcessed)}</dd></div>
              <div><dt>Last ACK</dt><dd>{fmtDateTime(h.components.bookingIngestion.lastAck)}</dd></div>
              <div><dt>Last webhook received</dt><dd>{fmtDateTime(h.components.bookingIngestion.lastWebhook)}</dd></div>
              <div><dt>Pending availability jobs</dt><dd>{h.metrics.availPending}</dd></div>
              <div><dt>Failed availability jobs</dt><dd>{h.metrics.availFailed}</dd></div>
              <div><dt>Pending ARI jobs</dt><dd>{h.metrics.ariPending}</dd></div>
              <div><dt>Failed ARI jobs</dt><dd>{h.metrics.ariFailed}</dd></div>
              <div><dt>Recent integration errors (24h)</dt><dd>{h.metrics.recentErrors}</dd></div>
              <div><dt>Properties with sync problems</dt><dd>{h.metrics.syncWarnProps}</dd></div>
            </dl>
          </div>
        </>
      )}</Async>
    </div>
  );
}

function HealthCard({ label, c, note }: { readonly label: string; readonly c: Json; readonly note?: string }): ReactElement {
  return <div className="pa-card"><span className="pa-card__label">{label}</span><span className="pa-health-status"><HealthDot status={c.status} />{c.status}{note ? <span className="pa-muted pa-small"> · {note}</span> : null}</span></div>;
}
function HealthDot({ status }: { readonly status: string }): ReactElement {
  const cls = status === "healthy" ? "pa-dot pa-dot--ok" : status === "warning" ? "pa-dot pa-dot--warn" : "pa-dot pa-dot--crit";
  return <span className={cls} aria-hidden="true" />;
}

function Activity(): ReactElement {
  const q = useData<Json>("/api/platform-admin/activity", { limit: 100 });
  return (
    <div>
      <h1 className="pa-h1">Activity</h1>
      <div className="pa-panel"><Async q={q} empty={<p className="pa-muted">No activity yet.</p>}>{(d) => <ActivityList rows={d.activity ?? []} full />}</Async></div>
    </div>
  );
}

function ActivityList({ rows, full }: { readonly rows: Json[]; readonly full?: boolean }): ReactElement {
  if (!rows || rows.length === 0) return <p className="pa-muted">No activity yet.</p>;
  return (
    <table className="pa-table"><thead><tr><th>Time</th><th>Actor</th><th>Organisation</th><th>Action</th><th>Result</th>{full && <th>Summary</th>}</tr></thead>
      <tbody>{rows.map((a: Json) => (
        <tr key={a.id}><td>{fmtDateTime(a.createdAt)}</td><td>{a.actorAdminEmail ?? "system"}</td><td>{a.organisationName ?? "—"}</td><td>{a.action}</td><td>{a.result === "error" ? <span className="pa-badge pa-badge--warn">error</span> : <span className="pa-badge pa-badge--ok">ok</span>}</td>{full && <td className="pa-muted">{a.summary ?? "—"}</td>}</tr>
      ))}</tbody></table>
  );
}

function PlatformAdmins(): ReactElement {
  const q = useData<Json>("/api/platform-admin/admins/list");
  return (
    <div>
      <h1 className="pa-h1">Platform admins</h1>
      <p className="pa-muted pa-small">Internal AVOS staff. Adding/editing platform admins is managed out-of-band (bootstrap script) for Release 1; this view is read-only.</p>
      <div className="pa-panel"><Async q={q} empty={<p className="pa-muted">No platform admins.</p>}>{(d) => (
        (d.users ?? []).length === 0 ? <p className="pa-muted">No platform admins.</p> :
        <table className="pa-table"><thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Last login</th><th>Created</th></tr></thead>
          <tbody>{d.users.map((u: Json) => (<tr key={u.id}><td>{u.email}</td><td>{u.role}</td><td><StatusBadge status={u.status} /></td><td>{fmtDateTime(u.lastLoginAt)}</td><td>{fmtDate(u.createdAt)}</td></tr>))}</tbody></table>
      )}</Async></div>
    </div>
  );
}

function StatusBadge({ status }: { readonly status: string }): ReactElement {
  const ok = ["active"]; const warn = ["suspended", "trial", "past_due"];
  const cls = ok.includes(status) ? "pa-badge pa-badge--ok" : warn.includes(status) ? "pa-badge pa-badge--warn" : "pa-badge";
  return <span className={cls}>{status}</span>;
}
